import React, { useLayoutEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface SettingsSideNavItem {
  /** Unique value/key for this tab */
  value: string;
  /** Visible label */
  label: string;
  /** Optional short description shown under the label */
  description?: string;
  /** Optional lucide icon shown to the left of the label */
  icon?: LucideIcon;
  /** Optional count badge shown on the right */
  count?: number;
  /** Optional tone for the count badge */
  tone?: 'default' | 'red' | 'orange' | 'yellow' | 'green';
}

interface SettingsSideNavProps {
  tabs: SettingsSideNavItem[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

const TONE_BADGE: Record<string, string> = {
  red: 'bg-red-500/15 text-red-300',
  orange: 'bg-orange-500/15 text-orange-300',
  green: 'bg-green-500/15 text-green-300',
  yellow: 'bg-yellow-500/15 text-yellow-300',
  default: 'bg-yellow-500/15 text-yellow-300',
};

/**
 * SettingsSideNav
 *
 * The vertical sidebar nav used across Settings (Fleet / Compliance Defaults,
 * IVR Settings, etc.) -- one item selected at a time, icon + label + short
 * description. A single gold indicator pill lives behind the list and slides
 * smoothly to whichever item is active (measured from the real DOM layout so
 * it tracks items of different heights), rather than each button just
 * toggling its own background. Pair with SettingsSidebarLayout for the full
 * sidebar + content-pane grid.
 */
const SettingsSideNav: React.FC<SettingsSideNavProps> = ({ tabs, value, onValueChange, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ top: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const activeEl = itemRefs.current[value];
    if (!container || !activeEl) return;

    const measure = () => {
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      setIndicator({ top: activeRect.top - containerRect.top, height: activeRect.height });
    };

    measure();

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, tabs]);

  return (
    <div ref={containerRef} className={`relative space-y-2 ${className ?? ''}`}>
      {indicator && (
        <div
          className="absolute left-0 right-0 z-0 rounded-xl border border-yellow-500/40 bg-zinc-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_20px_rgba(0,0,0,0.35)] transition-[top,height] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] lg:rounded-r-none lg:border-r-0"
          style={{ top: indicator.top, height: indicator.height }}
        />
      )}
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = value === tab.value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              itemRefs.current[tab.value] = el;
            }}
            type="button"
            onClick={() => onValueChange(tab.value)}
            aria-pressed={isActive}
            className={`relative z-10 w-full rounded-xl px-4 py-3 text-left transition-colors duration-200 ${
              isActive ? 'text-yellow-300' : 'text-gray-300 hover:bg-zinc-900/60 hover:text-yellow-300'
            }`}
          >
            <div className="flex items-center gap-3">
              {Icon && (
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200 ${
                    isActive ? 'border-black/20 bg-black/10' : 'border-yellow-900/30 bg-black/30'
                  }`}
                >
                  <Icon
                    key={isActive ? `${tab.value}-active` : `${tab.value}-inactive`}
                    className={`h-4 w-4 ${isActive ? 'animate-tab-icon-pop' : ''}`}
                  />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="block text-sm font-semibold leading-tight truncate">{tab.label}</span>
                  {typeof tab.count === 'number' && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-bold shrink-0 ${
                        isActive ? 'bg-black/15 text-yellow-200' : TONE_BADGE[tab.tone ?? 'default']
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </span>
                {tab.description && (
                  <span
                    className={`block text-xs leading-tight mt-0.5 ${
                      isActive ? 'text-yellow-100/70' : 'text-muted-foreground'
                    }`}
                  >
                    {tab.description}
                  </span>
                )}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default SettingsSideNav;
